import {openDb} from './BrowserDatabaseIDB';
import {convertEventVersion1To2} from "../components/Helpers/EventConverter";



export function ensureStoresExist(upgradeDb) {
    if (!upgradeDb.objectStoreNames.contains('Account')) {
        upgradeDb.createObjectStore('Account', { keyPath: 'id' });
    }
    if (!upgradeDb.objectStoreNames.contains('Contacts')) {
        upgradeDb.createObjectStore('Contacts', { keyPath: 'id' });
    }
    if (!upgradeDb.objectStoreNames.contains('Events')) {
        upgradeDb.createObjectStore('Events', { keyPath: 'id' });
    }
    if (!upgradeDb.objectStoreNames.contains('Templates')) {
        upgradeDb.createObjectStore('Templates', { keyPath: 'id' });
    }
    if (!upgradeDb.objectStoreNames.contains('Photos')) {
        const photoStore = upgradeDb.createObjectStore('Photos', { keyPath: 'id' });

        // These indexes allow you to query "Give me all photos for Event X"
        // or "Give me all photos for Attendee Y" very efficiently.
        photoStore.createIndex('event_id', 'event_id', { unique: false });
        photoStore.createIndex('attendee_id', 'attendee_id', { unique: false });
    }
}


export function getAllEventsFromDatabase(_user_id) {
    console.log('getAllEventsFromDatabase : ' + _user_id);

    // FIX: Check if the user ID is invalid/missing, and if so, return an empty array promise.
    if (_user_id === "" || _user_id === undefined || _user_id === null) {
        console.log('User ID is missing or invalid. Returning empty event list.');
        return Promise.resolve([]);
    }

    // 1. Ensure all required stores are created (with correct keyPaths)
    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        // 2. Create a readonly transaction spanning both required stores
        const tx = db.transaction(['Account', 'Events'], 'readonly');

        const accountStore = tx.objectStore('Account');
        const eventsStore = tx.objectStore('Events');

        // 3. CORRECTED: Get the Account record using the actual user ID (_user_id)
        // IndexedDB looks for the object whose 'id' field matches the value of _user_id.
        const accountPromise = accountStore.get(_user_id);

        // Get all events
        const eventsPromise = eventsStore.getAll();

        // Use Promise.all to wait for both database operations to complete
        return Promise.all([accountPromise, eventsPromise]);

    }).then(function ([_account, _events]) {
        // 4. Verification check is now redundant but kept for safety
        if (_account && _account.id === _user_id) {
            return _events; // Returns the array of all event objects
        } else {
            // This case should primarily catch when the account doesn't exist
            console.log('Account not found for ID:', _user_id);
            return null;
        }
    }).catch(function (error) {
        console.error("An error occurred:", error);
        throw error;
    });
}
export function getEventFromDatabase(eventId) {
    console.log('getEventFromDatabase : '+eventId);

    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        const tx = db.transaction('Events', 'readwrite'); // Use 'readwrite' mode for updates
        const store = tx.objectStore('Events');

        return store.get(eventId);
    }).then(function (_event) {
        if (_event) {
            console.log('Event found:', _event);
            return _event;
        } else {
            console.log('Event not found with ID:', eventId);
            return null;
        }
    }).catch(function (error) {
        console.error("An error occurred:", error);
        throw error; // Re-throw the error to be caught by the caller
    });
}
export function browserStorageAddEvent(newEvent) {
    console.log('browserStorageAddEvent: adding or updating event ID:', newEvent.id);

    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        // 1. Create a readwrite transaction for the 'Events' store.
        const tx = db.transaction('Events', 'readwrite');
        const store = tx.objectStore('Events');

        // 2. Use store.put() on the single new event object.
        // If an event with newEvent.id exists, it will be updated (overwritten).
        // If it doesn't exist, it will be added. This is atomic and fast.
        const sanitizedEvent = JSON.parse(JSON.stringify(newEvent));
        const putPromise = store.put(sanitizedEvent);

        // 3. Wait for the put operation AND the entire transaction to complete.
        return Promise.all([putPromise, tx.complete]);

    }).then(function () {
        // The promise resolves once the data is successfully written and the transaction commits.
        console.log("Event ID:", newEvent.id, "added/updated successfully.");
        // The result of this function is intentionally simple (true) to indicate success.
        return true;
    }).catch(function (error) {
        // Log the error and re-throw it.
        console.error("An error occurred while adding/updating the event:", error);
        throw error;
    });
}
export async function browserStorageUpdateAllEvents(_family_events) {
    console.log(`browserStorageUpdateAllEvents: processing ${_family_events.length} events for update/insert.`);

    try {
        const db = await openDb('pa', 1, ensureStoresExist);

        // 1. Create a single 'readwrite' transaction.
        const tx = db.transaction('Events', 'readwrite');
        const store = tx.objectStore('Events');

        // 2. Map the array of incoming events to an array of individual database promises.
        // Each event is written/overwritten using its 'id' key.
        const writePromises = _family_events.map(event => {
            // store.put() handles both 'add' (new event) and 'update' (existing event).
            return store.put(event);
        });

        // 3. Wait for all individual write operations to start (not complete).
        // Then, wait for the entire transaction to commit.
        await Promise.all(writePromises);
        await tx.complete;

        console.log("All events successfully updated/inserted in IndexedDB. ✅");

        // Return the array of events just processed, consistent with the original function's intent.
        return _family_events;

    } catch (error) {
        console.error("An error occurred during bulk event update:", error);
        throw error;
    }
}

export function browserStorageUpdateSpecificEvent(_event) {
    console.log("browserStorageUpdateSpecificEvent: updating event:", _event);

    if (!_event || !_event.id) {
        console.error("browserStorageUpdateSpecificEvent: event with valid 'id' is required.");
        return Promise.reject(new Error("Event must have an 'id'."));
    }

    // If needed, convert old event shape (v1 → v2)
    const convertIfNeeded = (ev) => (ev && ev.title ? ev : convertEventVersion1To2(ev));
    const next = convertIfNeeded(_event);

    // Sanitize to ensure it's safe to store in IndexedDB (no functions, weird objects, etc.)
    const sanitizedEvent = JSON.parse(JSON.stringify(next));

    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const store = tx.objectStore("Events");

            // For keyPath "id", we just put the object; IndexedDB uses sanitizedEvent.id as key
            return store.put(sanitizedEvent).then(() => {
                console.log(`Event with ID ${sanitizedEvent.id} added/updated successfully.`);
                return true;
            });
        })
        .catch(function (error) {
            console.error("An error occurred in browserStorageUpdateSpecificEvent:", error);
            throw error;
        });
}

export function browserStorageUpdateSpecificEventField(_event_id, _field_name, _field_value) {
    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        const tx = db.transaction('Events', 'readwrite');
        const store = tx.objectStore('Events');

        // Get the events array by key "Events"
        return store.get('Events').then(eventsArray => {
            if (!eventsArray) {
                eventsArray = [];
            }

            // Find the index of the event if it exists
            const eventIndex = eventsArray.findIndex(event => event.id === _event_id);
            console.log("eventIndex : "+eventIndex);

            eventsArray[eventIndex][_field_name] = _field_value;

            console.log("eventsArray : ", eventsArray);

            const sanitizedEventsArray = JSON.parse(JSON.stringify(eventsArray));

            // Put the updated events array back into the object store
            return store.put(sanitizedEventsArray, 'Events').then(() => {
                console.log(`Event with ID ${_event_id} added/updated successfully.`);
                return true;
            });
        });
    }).catch(function (error) {
        console.error("An error occurred:", error);
        throw error; // Re-throw the error to be caught by the caller
    });
}
export function browserStorageReplacePlanningWithEvent(newly_created_event) {
    console.log("browserStorageReplacePlanningWithEvent:", newly_created_event);

    const tempId = newly_created_event.temp_id;
    const realId = newly_created_event.id;

    if (!tempId) {
        console.error("browserStorageReplacePlanningWithEvent: 'temp_id' is required.");
        return Promise.reject(new Error("'temp_id' is missing on newly_created_event."));
    }

    if (!realId) {
        console.error("browserStorageReplacePlanningWithEvent: 'id' is required.");
        return Promise.reject(new Error("'id' is missing on newly_created_event."));
    }

    // Make sure what we store is IndexedDB-safe (plain JSON)
    const sanitizedEvent = JSON.parse(JSON.stringify(newly_created_event));

    return openDb("pa", 1, ensureStoresExist)
        .then(db => {
            const tx = db.transaction("Events", "readwrite");
            const store = tx.objectStore("Events");

            // 1) Delete the old temp event (stored under tempId as `id`)
            const deletePromise = store.delete(tempId).catch(err => {
                // Not fatal if it doesn't exist; log and continue
                console.warn(`Failed to delete temp event ${tempId} (may not exist):`, err);
            });

            // 2) Put the new event under its real id (keyPath 'id' will use sanitizedEvent.id)
            const putPromise = store.put(sanitizedEvent);

            return Promise.all([deletePromise, putPromise]).then(() => {
                console.log(
                    `Replaced planning event temp_id=${tempId} with real event id=${realId} in IndexedDB.`
                );
                return true;
            });
        })
        .catch(error => {
            console.error("An error occurred in browserStorageReplacePlanningWithEvent:", error);
            throw error;
        });
}


export function removeAllEventsFromDatabase() {
    return openDb('pa', 1, ensureStoresExist).then(function(db) {
        const tx = db.transaction('Events', 'readwrite');
        const store = tx.objectStore('Events');

        // Delete any contents in the Events array
        store.clear();

        // 3. Wait for the transaction to finish and commit
        return tx.complete;
    }).then(function() {
        console.log('Events removed from Events store.');
    }).catch(function(error) {
        console.error('An error occurred while removing events:', error);
        throw error; // Re-throw the error to be caught by the caller
    });
}

export function getAllTemplatesFromDatabase() {
    console.log('Fetching all templates from Templates');

    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        const tx = db.transaction('Templates', 'readonly');
        const store = tx.objectStore('Templates');

        // return all templates
        return store.getAll();
    }).catch(error => {
        console.error("An error occurred while fetching templates:", error);
        throw error;
    });
}
export function getTemplateFromDatabase(templateId) {
    console.log('Fetching template:', templateId);

    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        const tx = db.transaction('Templates', 'readonly');
        const store = tx.objectStore('Templates');

        return store.get(templateId);
    }).then(template => {
        if (template) {
            console.log('Template found:', template);
            return template;
        } else {
            console.log('Template not found with ID:', templateId);
            return null;
        }
    }).catch(error => {
        console.error("An error occurred while fetching the template:", error);
        throw error;
    });
}
export function browserStorageAddTemplate(newTemplate) {
    return openDb('pa', 1, function (upgradeDb) {
        if (!upgradeDb.objectStoreNames.contains('Account')) {
            // FIX: Set keyPath to 'id' so we can look up by user ID
            upgradeDb.createObjectStore('Account', { keyPath: 'id' });
        }
        if (!upgradeDb.objectStoreNames.contains('Events')) {
            upgradeDb.createObjectStore('Events', { keyPath: 'id' });
        }
        if (!upgradeDb.objectStoreNames.contains('Photos')) {
            upgradeDb.createObjectStore('Photos');
        }
    }).then(function (db) {
        const tx = db.transaction('pa_data', 'readwrite');
        const store = tx.objectStore('pa_data');

        return store.get('Templates').then(existingTemplates => {
            console.log("existingTemplates : ", existingTemplates);

            const templates = existingTemplates || []; // Ensure we have an array

            // Check if a template with the same ID already exists
            const templateExists = templates.some(template => template.id === newTemplate.id);

            if (templateExists) {
                console.log("Template with the same ID already exists. Skipping addition.");
                return templates; // Return the existing templates unchanged
            }

            // Add the new template to the array
            templates.push(JSON.parse(JSON.stringify(newTemplate)));

            // Store the updated array back into pa_data
            return store.put(templates, "Templates").then(() => {
                console.log("New template added successfully.");
                return templates;
            });
        });
    }).catch(error => {
        console.error("An error occurred while adding a template:", error);
        throw error;
    });
}
export function browserStorageUpdateTemplate(updatedTemplate) {

    return openDb('pa', 1, function (upgradeDb) {
        if (!upgradeDb.objectStoreNames.contains('Account')) {
            // FIX: Set keyPath to 'id' so we can look up by user ID
            upgradeDb.createObjectStore('Account', { keyPath: 'id' });
        }
        if (!upgradeDb.objectStoreNames.contains('Events')) {
            upgradeDb.createObjectStore('Events', { keyPath: 'id' });
        }
        if (!upgradeDb.objectStoreNames.contains('Photos')) {
            upgradeDb.createObjectStore('Photos');
        }
    }).then(function (db) {
        const tx = db.transaction('pa_data', 'readwrite');
        const store = tx.objectStore('pa_data');

        return store.get('Templates').then(templatesArray => {
            if (!templatesArray) {
                templatesArray = [];
            }
            console.log("templatesArray : ",templatesArray);

            // Find the index of the template if it exists
            const templateIndex = templatesArray.findIndex(template => template.template_id === updatedTemplate.template_id);
            console.log("templateIndex : ",templateIndex);

            if (templateIndex !== -1) {
                // Update the existing template
                templatesArray[templateIndex] = JSON.parse(JSON.stringify(updatedTemplate));
            } else {
                // Add the new template
                templatesArray.push(JSON.parse(JSON.stringify(updatedTemplate)));
            }

            console.log("templatesArray after: ",templatesArray);

            // Put the updated templates array back into pa_data
            return store.put(templatesArray, 'Templates').then(() => {
                console.log(`Template with ID ${updatedTemplate.template_id} added/updated successfully.`);
                return true;
            });
        });
    }).catch(error => {
        console.error("An error occurred while updating the template:", error);
        throw error;
    });
}
export async function browserStorageUpdateTemplates(changesArray) {
    console.log('browserStorageUpdateTemplates: processing template changes.');

    try {
        const db = await openDb('pa', 1, ensureStoresExist);

        // 1. Create a single 'readwrite' transaction for all changes.
        const tx = db.transaction('Templates', 'readwrite');
        const store = tx.objectStore('Templates');

        // 2. Map the array of changes to an array of individual database promises.
        const writePromises = changesArray.map(change => {
            const { template_id, change_type, data } = change;

            if (change_type === "new" || change_type === "updated") {
                // Use .put() to add or overwrite the single record.
                // Since keyPath is 'id' (or 'template_id' if defined as such), the key is in the data object.
                // Assuming the template object is available in change.data and has a key:
                if (data && data.id) { // Assuming keyPath is 'id'
                    return store.put(data);
                } else if (data && data.template_id) { // Assuming keyPath is 'template_id'
                    return store.put(data);
                } else {
                    console.error("Template data is missing required key for update:", data);
                    return Promise.resolve(); // Ignore bad data
                }
            } else if (change_type === "deleted") {
                // Use .delete() to remove the single record using its ID.
                return store.delete(template_id);
            }
            return Promise.resolve();
        });

        // 3. Wait for all put/delete operations to initiate, then wait for the transaction to complete.
        await Promise.all(writePromises);
        await tx.complete;

        console.log("All templates updated successfully in IndexedDB. ✅");
        return true;

    } catch (error) {
        console.error("An error occurred while updating templates:", error);
        // The error is already logged, re-throwing ensures the caller handles the failure.
        throw error;
    }
}
export function removeTemplateFromDatabase(templateId) {
    return openDb('pa', 1, function (upgradeDb) {
        if (!upgradeDb.objectStoreNames.contains('Account')) {
            // FIX: Set keyPath to 'id' so we can look up by user ID
            upgradeDb.createObjectStore('Account', { keyPath: 'id' });
        }
        if (!upgradeDb.objectStoreNames.contains('Events')) {
            upgradeDb.createObjectStore('Events', { keyPath: 'id' });
        }
        if (!upgradeDb.objectStoreNames.contains('Photos')) {
            upgradeDb.createObjectStore('Photos');
        }
    }).then(function (db) {
        const tx = db.transaction('pa_data', 'readwrite');
        const store = tx.objectStore('pa_data');

        return store.get('Templates').then(templates => {
            if (!templates) return false;

            // Filter out the template with the matching ID
            const updatedTemplates = templates.filter(tmpl => tmpl.template_id !== templateId);

            // Store the updated templates array back into pa_data
            return store.put(updatedTemplates, 'Templates').then(() => {
                console.log(`Template with ID ${templateId} removed successfully.`);
                return true;
            });
        });
    }).catch(error => {
        console.error("An error occurred while removing the template:", error);
        throw error;
    });
}




export function addEventPhotoThumbnail(_photo) {
    console.log('addEventPhotoThumbnail');

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }

        }).then(function(db) {
            const tx = db.transaction('Photos', 'readwrite');
            const photosStore = tx.objectStore('Photos');

            // Retrieve the existing array of photos for the event_id
            photosStore.get(_photo["event_id"]).then(function(existingPhotosArray) {
                existingPhotosArray = existingPhotosArray || []; // If no array exists, initialize to empty array

                //find the Photo and update the thumbnail data
                let photoUpdated = false;
                for (let photo of existingPhotosArray) {
                    if (photo.event_id === _photo["event_id"] && photo.attendee_id === _photo["attendee_id"] && photo.id === _photo["id"]) {
                        photo.thumbnail_data = _photo["thumbnail_data"];
                        photoUpdated = true;
                        break;
                    }
                }

                if (!photoUpdated){
                    //didn't find the photo to update so need to add it instead
                    existingPhotosArray.push(_photo);
                }

                // Store the updated array back in the database
                photosStore.put(existingPhotosArray, _photo["event_id"]).then(function() {
                    console.log('Photo added to the array for event_id ' + _photo["event_id"]);
                    resolve(true);
                }).catch(function(error) {
                    console.error('Error storing updated array:', error);
                    reject(error);
                });
            }).catch(function(error) {
                console.error('Error retrieving existing array:', error);
                reject(error);
            });


            // If entry doesn't exist, create it
            tx.oncomplete = function(event) {
                console.log('tx.oncomplete: ' + event);
                if (!event.target.result) {
                    const newPhotosArray = [_photo];
                    const addRequest = photosStore.put(newPhotosArray, _photo["event_id"]);
                    addRequest.onsuccess = function(event) {
                        console.log('New array created for event_id ' + _photo["event_id"]);
                        resolve(true);
                    };
                    addRequest.onerror = function(event) {
                        console.error('Error creating new array:', event.target.error);
                        reject(event.target.error);
                    };
                }
            };
        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}

export function getAllPhotosForEvent(eventId) {
    return new Promise(function(resolve, reject) {
        openDb('pa', 1).then(function (upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }
        }).then(function(db) {
            const tx = db.transaction('pa_photos', 'readonly');
            const photosStore = tx.objectStore('pa_photos');

            // Retrieve the array of photos for the specified event_id
            const getRequest = photosStore.get(eventId);

            getRequest.then(function(result) {
                if (result === undefined) {
                    resolve([]); // Resolve with an empty array if no photos found
                } else {
                    resolve(result); // Resolve with the array of photos
                }
            }).catch(function(error) {
                console.error('Error retrieving photos:', error);
                reject(error);
            });
        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function getAllPhotosForEventAttendee(eventId, attendeeId) {
    return new Promise(function(resolve, reject) {
        openDb('pa', 1).then(function (upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }
        }).then(function(db) {
            const tx = db.transaction('pa_photos', 'readonly');
            const photosStore = tx.objectStore('pa_photos');

            // Retrieve the array of photos for the specified event_id
            const getRequest = photosStore.get(eventId);

            getRequest.then(function(result) {
                if (result === undefined) {
                    resolve([]); // Resolve with an empty array if no photos found
                } else {
                    resolve(result.filter(photo => photo.attendee_id === attendeeId)); // Resolve with the array of photos
                }
            }).catch(function(error) {
                console.error('Error retrieving photos:', error);
                reject(error);
            });
        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function getSpecificEventPhotoDetails(eventId, _photo_id) {
    console.log('getSpecificEventPhotoDetails');
    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }

        }).then(function(db) {
            const tx = db.transaction('pa_data', 'readonly');
            const dataStore = tx.objectStore('pa_data');

            // get the Event from the Events array
            const getRequest = dataStore.get('Events');
            getRequest.then(function(result) {
                const event = result.find(evt => evt.id === eventId);
                console.log('event:', event);

                if (!event) {
                    return reject(`Event with ID ${eventId} not found`);
                }

                console.log('Found the Event; looking for photo id:', _photo_id);

                // Get the specific photo from the list of photos in the event
                const photo = event.photos.find(p => p.id === _photo_id);
                console.log('photo:', photo);

                if (!photo) {
                    return reject(`Photo with ID ${_photo_id} not found in Event ${eventId}`);
                }

                resolve(photo);
            }).catch(function(error) {
                console.error('Error retrieving photos:', error);
                reject(error);
            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function getSpecificPhotoForEvent(eventId, _photo_id) {
    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {

        }).then(function(db) {
            const tx = db.transaction('Photos', 'readonly');
            const photosStore = tx.objectStore('Photos');

            // Retrieve the array of photos for the specified event_id
            const getRequest = photosStore.get(eventId);

            getRequest.then(function(result) {
                if (result === undefined) {
                    resolve(null); // Resolve with an empty array if no photos found
                } else {
                    resolve(result.find((_photo) => _photo.id === _photo_id)); // Resolve with the specific photo
                }
            }).catch(function(error) {
                console.error('Error retrieving photos:', error);
                reject(error);
            });
        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function updatePhotoInProgress(eventId, _photo_id) {
    console.log("updatePhotoInProgress : "+eventId+" : "+_photo_id);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }

        }).then(function(db) {
            console.log("got the db : "+db);

            const tx = db.transaction('pa_photos', 'readwrite');
            const photosStore = tx.objectStore('pa_photos');

            console.log("photosStore : "+photosStore);

            // Retrieve the array of photos by event ID
            photosStore.get(eventId).then(function(photoArray) {

                const photo = photoArray.find(photo => photo.id === _photo_id);

                console.log("the photo to update : "+photo);

                if (photo) {
                    // Update the property value
                    photo.full_size_uploading_in_progress = true;

                    // Put the updated photo object back into the object store
                    photosStore.put(photoArray, eventId).then(function(result) {
                        console.log("saving updated photo : "+result);
                        resolve(true);
                    });


                } else {
                    // Photo object not found, reject with an error
                    const error = new Error('Photo not found');
                    console.error('Error updating photo:', error);
                    reject(error);
                }
            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function addEventAttendeePhotoToBrowserStorage(eventId, _photo) {
    console.log("addEventAttendeePhotoToBrowserStorage : "+eventId);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }
        }).then(function(db) {

            const tx = db.transaction('pa_data', 'readwrite');
            const dataStore = tx.objectStore('pa_data');

            // Retrieve the array of photos by event ID
            dataStore.get("Events").then(function(eventsArray) {
                const _event = eventsArray.find(event => event.id === eventId);
                if (_event) {
                    //add the photo to the Event
                    _event['photos'].push(_photo);

                    // Put the updated Events array back into the object store
                    dataStore.put(eventsArray, "Events").then(function(result) {
                        console.log("successfully added photo to Event : "+result);
                        resolve(true);
                    });


                } else {
                    // Event object not found, reject with an error
                    const error = new Error('Event not found');
                    console.error('Error adding Event photo:', error);
                    reject(error);
                }
            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}

export function updateEventAttendeePhotoInBrowserStorageUploadingInProgress(eventId, _photo_id, _in_progress ) {
    console.log("updateEventAttendeePhotoInBrowserStorageUploadingInProgress : "+eventId+" : "+_photo_id);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }
        }).then(function(db) {

            const tx = db.transaction('pa_data', 'readwrite');
            const _dataStore = tx.objectStore('pa_data');

            // Retrieve the array of photos by event ID
            _dataStore.get("Events").then(function(eventsArray) {
                const _event = eventsArray.find(event => event.id === eventId);
                if (_event) {
                    //update the Event attendee photo
                    const photo = _event['photos'].find(photo => photo.id === _photo_id);

                    if (photo) {
                        // Update the property value
                        photo.full_size_uploading_in_progress = _in_progress;

                        // Put the updated photo object back into the object store
                        _dataStore.put(eventsArray, "Events").then(function(result) {
                            console.log("saving updated Events : "+result);
                            resolve(true);
                        });

                    } else {
                        // Photo object not found, reject with an error
                        const error = new Error('Photo not found');
                        console.error('Error updating Event photo:', error);
                        reject(error);
                    }

                } else {
                    // Event object not found, reject with an error
                    const error = new Error('Event not found');
                    console.error('Error adding Event photo:', error);
                    reject(error);
                }
            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function updateEventAttendeePhotoInBrowserStoragePaDataPrivate(eventId, _photo_id, _private ) {
    console.log("updateEventAttendeePhotoInBrowserStoragePaDataPrivate : "+eventId+" : "+_photo_id);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }

        }).then(function(db) {

            const tx = db.transaction('pa_data', 'readwrite');
            const _dataStore = tx.objectStore('pa_data');

            // Retrieve the array of photos by event ID
            _dataStore.get("Events").then(function(eventsArray) {
                const _event = eventsArray.find(event => event.id === eventId);
                if (_event) {
                    //update the Event attendee photo
                    const photo = _event['photos'].find(photo => photo.id === _photo_id);

                    if (photo) {
                        // Update the property value
                        photo.private = _private;

                        // Put the updated photo object back into the object store
                        _dataStore.put(eventsArray, "Events").then(function(result) {
                            console.log("saving updated Events : "+result);
                            resolve(true);
                        });

                    } else {
                        // Photo object not found, reject with an error
                        const error = new Error('Photo not found');
                        console.error('Error updating Event photo:', error);
                        reject(error);
                    }

                } else {
                    // Event object not found, reject with an error
                    const error = new Error('Event not found');
                    console.error('Error adding Event photo:', error);
                    reject(error);
                }
            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function updateEventAttendeePhotoInBrowserStoragePaPhotosPrivate(eventId, _photo_id, _private ) {
    console.log("updateEventAttendeePhotoInBrowserStoragePaPhotosPrivate : "+eventId+" : "+_photo_id);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }
        }).then(function(db) {

            const tx = db.transaction('pa_photos', 'readwrite');
            const _photosStore = tx.objectStore('pa_photos');

            // Retrieve the array of photos by event ID
            _photosStore.get(eventId).then(function(photosArray) {
                const _photo = photosArray.find(photo => photo.id === _photo_id);

                //update the Event attendee photo
                if (_photo) {
                    // Update the property value
                    _photo.private = _private;

                    // Put the updated photo object back into the object store
                    _photosStore.put(photosArray, eventId).then(function(result) {
                        console.log("saving updated Photo : "+result);
                        resolve(true);
                    });

                } else {
                    // Photo object not found, reject with an error
                    const error = new Error('Photo not found');
                    console.error('Error updating Event photo:', error);
                    reject(error);
                }

            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}

export function updateEventAttendeePhotoInBrowserStoragePaDataSharable(eventId, _photo_id, _sharable, _share_with ) {
    console.log("updateEventAttendeePhotoInBrowserStoragePaDataSharable : "+eventId+" : "+_photo_id);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }
        }).then(function(db) {

            const tx = db.transaction('pa_data', 'readwrite');
            const _dataStore = tx.objectStore('pa_data');

            // Retrieve the array of photos by event ID
            _dataStore.get("Events").then(function(eventsArray) {
                const _event = eventsArray.find(event => event.id === eventId);
                if (_event) {
                    //update the Event attendee photo
                    const photo = _event['photos'].find(photo => photo.id === _photo_id);

                    if (photo) {
                        // Update the property value
                        photo.sharable = _sharable;
                        photo.share_with = _share_with;

                        // Put the updated photo object back into the object store
                        _dataStore.put(eventsArray, "Events").then(function(result) {
                            console.log("saving updated Events : "+result);
                            resolve(true);
                        });

                    } else {
                        // Photo object not found, reject with an error
                        const error = new Error('Photo not found');
                        console.error('Error updating Event photo:', error);
                        reject(error);
                    }

                } else {
                    // Event object not found, reject with an error
                    const error = new Error('Event not found');
                    console.error('Error adding Event photo:', error);
                    reject(error);
                }
            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}
export function updateEventAttendeePhotoInBrowserStoragePaPhotosSharable(eventId, _photo_id, _sharable, _share_with ) {
    console.log("updateEventAttendeePhotoInBrowserStoragePaPhotosSharable : "+eventId+" : "+_photo_id);

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }

        }).then(function(db) {

            const tx = db.transaction('pa_photos', 'readwrite');
            const _photosStore = tx.objectStore('pa_photos');

            // Retrieve the array of photos by event ID
            _photosStore.get(eventId).then(function(photosArray) {
                const _photo = photosArray.find(photo => photo.id === _photo_id);

                //update the Event attendee photo
                if (_photo) {
                    // Update the property value
                    _photo.sharable = _sharable;
                    _photo.share_with = _share_with;

                    // Put the updated photo object back into the object store
                    _photosStore.put(photosArray, eventId).then(function(result) {
                        console.log("saving updated Photo : "+result);
                        resolve(true);
                    });

                } else {
                    // Photo object not found, reject with an error
                    const error = new Error('Photo not found');
                    console.error('Error updating Event photo:', error);
                    reject(error);
                }

            });

        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}

export function updateEventPhoto(event_id, _updated_photo) {
    console.log("updateEventPhoto");

    return new Promise(function(resolve, reject) {
        openDb('pa', 1, function(upgradeDb) {
            if (!upgradeDb.objectStoreNames.contains('Account')) {
                // FIX: Set keyPath to 'id' so we can look up by user ID
                upgradeDb.createObjectStore('Account', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Events')) {
                upgradeDb.createObjectStore('Events', { keyPath: 'id' });
            }
            if (!upgradeDb.objectStoreNames.contains('Photos')) {
                upgradeDb.createObjectStore('Photos');
            }

        }).then(function(db) {
            const tx = db.transaction('pa_photos', 'readwrite');
            const photosStore = tx.objectStore('pa_photos');

            // Retrieve the array of photos for the specified event_id
            const getRequest = photosStore.get(event_id);

            getRequest.then(function(eventPhotos) {
                console.log("eventPhotos : "+eventPhotos);

                if (eventPhotos === undefined) {
                    // If eventPhotos is undefined, initialize it with the updated photo object
                    const updatedEventPhotos = [_updated_photo];
                    const putRequest = photosStore.put(updatedEventPhotos, event_id);
                    putRequest.onsuccess = function(event) {
                        console.log('Event with id ' + event_id + ' not found. New entry created.');
                        resolve(_updated_photo);
                    };
                    putRequest.onerror = function(event) {
                        console.error('Error creating new entry:', event.target.error);
                        reject(event.target.error);
                    };
                } else {
                    // Find the index of the photo object by its id
                    const photoIndex = eventPhotos.findIndex(photo => photo.id === _updated_photo.id);

                    if (photoIndex !== -1) {
                        // Replace the existing photo object with the updated one
                        eventPhotos[photoIndex] = _updated_photo;
                    } else {
                        // Add the updated photo object to the array
                        eventPhotos.push(_updated_photo);
                    }

                    // Put the updated eventPhotos array back into the object store
                    const putRequest = photosStore.put(eventPhotos, event_id);
                    putRequest.onsuccess = function(event) {
                        console.log('Photo updated:', _updated_photo);
                        resolve(_updated_photo);
                    };
                    putRequest.onerror = function(event) {
                        console.error('Error updating photo:', event.target.error);
                        reject(event.target.error);
                    };
                }
            }).catch(function(error) {
                console.error('Error retrieving photos for event:', error);
                reject(error);
            });
        }).catch(function(error) {
            console.error('Error:', error);
            reject(error);
        });
    });
}



export function addEventMessage(_event_id, _message){

    return openDb('pa', 1, ensureStoresExist).then(function (db) {
        const tx = db.transaction('Events', 'readwrite');
        const dataStore = tx.objectStore('Events');

        // Get the events array by key "Events"
        return dataStore.get('Events').then(eventsArray => {
            if (!eventsArray) {
                eventsArray = [];
            }

            // Find the event by ID
            const eventIndex = eventsArray.findIndex(event => event.id === _event_id);

            if (eventIndex !== -1) {
                // Get the specific event
                const event = eventsArray[eventIndex];

                // Check if the message already exists in the event
                const messageExists = event.messages && event.messages.some(msg => msg.id === _message.id);

                if (!messageExists) {
                    // Add the Message to the Event if it doesn't already exist
                    event.messages.push(_message);

                    // Find the latest timestamp from the Event messages and update
                    if (event.messages.length > 0) {
                         // Start with 0 or an initial very old timestamp
                        event.latest_message_timestamp = event.messages.reduce((latest, msg) => {
                            return latest > msg.timestamp ? latest : msg.timestamp;
                        }, 0);
                    }

                    // Update the event in the events array
                    eventsArray[eventIndex] = event;

                    // Put the updated events array back into the object store
                    return dataStore.put(eventsArray, 'Events').then(() => {
                        console.log(`Message with ID ${_message.id} in Event with ID ${_event_id} added successfully.`);
                        return true;
                    });
                } else {
                    console.log(`Message with ID ${_message.id} already exists in Event with ID ${_event_id}.`);
                    return false;
                }
            } else {
                console.error(`Event with ID ${_event_id} not found.`);
                return false;
            }
        });
    }).catch(function (error) {
        console.error("An error occurred:", error);
        throw error; // Re-throw the error to be caught by the caller
    });
}

// returns { merged, changed }
export function mergeChanged(prev, patch) {
    const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

    // If patch is undefined, don't touch prev
    if (typeof patch === "undefined") return { merged: prev, changed: false };

    // If types differ or either is not an object, replace when values differ
    if (!isObj(prev) || !isObj(patch)) {
        const changed = !isShallowEqual(prev, patch);
        return { merged: changed ? patch : prev, changed };
    }

    // Recurse on plain objects
    let changed = false;
    const out = { ...prev };

    // Only iterate keys present in patch (we don't delete)
    Object.keys(patch).forEach((k) => {
        const pv = prev[k];
        const nv = patch[k];

        if (typeof nv === "undefined") {
            // ignore undefined to preserve existing
            return;
        }

        if (Array.isArray(nv) || Array.isArray(pv)) {
            // Arrays: replace only if different (cheap compare)
            if (!arraysShallowEqual(pv, nv)) {
                out[k] = nv;
                changed = true;
            }
            return;
        }

        if (isObj(pv) && isObj(nv)) {
            const r = mergeChanged(pv, nv);
            if (r.changed) {
                out[k] = r.merged;
                changed = true;
            }
        } else {
            if (!isShallowEqual(pv, nv)) {
                out[k] = nv;
                changed = true;
            }
        }
    });

    return { merged: changed ? out : prev, changed };
}

function isShallowEqual(a, b) {
    // handles primitives & reference equality for objects
    return a === b || (Number.isNaN(a) && Number.isNaN(b));
}

function arraysShallowEqual(a, b) {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (!isShallowEqual(a[i], b[i])) return false;
    }
    return true;
}
